import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AdminUserData } from '../../pages/SetupWizardPage'

interface Step4AdminUserProps {
  initialData: AdminUserData | null
  userEmail: string
  onSubmit: (data: AdminUserData) => void
  onBack: () => void
  loading: boolean
}

export default function Step4AdminUser({
  initialData,
  userEmail,
  onSubmit,
  onBack,
  loading,
}: Step4AdminUserProps) {
  const { t } = useTranslation()
  const [useCurrentUser, setUseCurrentUser] = useState(
    initialData ? initialData.email === userEmail : true
  )
  const [email, setEmail] = useState(initialData?.email || userEmail)
  const [name, setName] = useState(initialData?.name || '')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!useCurrentUser) {
      if (!email.trim()) {
        newErrors.email = 'Sähköpostiosoite on pakollinen'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        newErrors.email = t('validation.invalidEmail')
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (validate()) {
      onSubmit({
        email: useCurrentUser ? userEmail : email,
        name: useCurrentUser ? undefined : name,
        invite_only: !useCurrentUser,
      })
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Admin-käyttäjä</h2>
      <p className="text-gray-600 mb-6">Määritä organisaation pääkäyttäjä. Pääkäyttäjä voi hallita kaikkia asetuksia ja kutsua muita käyttäjiä.</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Current user option */}
        <div
          onClick={() => setUseCurrentUser(true)}
          className={`
            p-4 border-2 rounded-xl cursor-pointer transition-all
            ${useCurrentUser
              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100'
              : 'border-gray-200 hover:border-blue-300'
            }
          `}
        >
          <div className="flex items-center">
            <div className={`
              w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center
              ${useCurrentUser ? 'border-blue-500' : 'border-gray-300'}
            `}>
              {useCurrentUser && <div className="w-3 h-3 rounded-full bg-blue-500"></div>}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Käytä nykyistä käyttäjää</h3>
              <p className="text-sm text-gray-600 mt-1">
                Sinut ({userEmail}) asetetaan automaattisesti pääkäyttäjäksi.
              </p>
            </div>
          </div>
        </div>

        {/* Invite another user option */}
        <div
          onClick={() => setUseCurrentUser(false)}
          className={`
            p-4 border-2 rounded-xl cursor-pointer transition-all
            ${!useCurrentUser
              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100'
              : 'border-gray-200 hover:border-blue-300'
            }
          `}
        >
          <div className="flex items-center">
            <div className={`
              w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center
              ${!useCurrentUser ? 'border-blue-500' : 'border-gray-300'}
            `}>
              {!useCurrentUser && <div className="w-3 h-3 rounded-full bg-blue-500"></div>}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Kutsu toinen käyttäjä</h3>
              <p className="text-sm text-gray-600 mt-1">
                Lähetä kutsu toiselle henkilölle pääkäyttäjäksi.
              </p>
            </div>
          </div>
        </div>

        {/* Invite form */}
        {!useCurrentUser && (
          <div className="space-y-4 pl-4 border-l-2 border-blue-200">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Sähköpostiosoite <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (errors.email) setErrors({})
                }}
                placeholder="admin@example.com"
                className={`
                  w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2
                  ${errors.email
                    ? 'border-red-300 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-blue-200 focus:border-blue-400'
                  }
                `}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Nimi (valinnainen)
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Matti Meikäläinen"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <span className="font-medium">Huom:</span> Kutsu lähetetään antamaasi sähköpostiosoitteeseen.
                Vastaanottajan täytyy rekisteröityä ja hyväksyä kutsu ennen kuin pääsee käyttämään organisaatiota.
              </p>
            </div>
          </div>
        )}

        {/* Info box */}
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Pääkäyttäjän oikeudet</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Hallinnoi organisaation asetuksia</li>
            <li>• Kutsu ja hallinnoi muita käyttäjiä</li>
            <li>• Määritä käyttäjäroolit ja oikeudet</li>
            <li>• Hallinnoi tilikausia ja kirjanpitoa</li>
            <li>• Näe kaikki raportit ja tiedot</li>
          </ul>
        </div>

        {/* Buttons */}
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            ← Takaisin
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Tallennetaan...
              </>
            ) : (
              <>Seuraava →</>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
